/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: select-shadcn.tsx
 🗓️ Created: 2025-01-10
 🧩 Description:
    Select component compatible with Shadcn API but without Radix UI.
    Built with React and framer-motion for consistency.

 🔧 Notes:
    - Compatible API with Shadcn select
    - No external dependencies beyond framer-motion
    - Fully accessible with keyboard navigation
    - Consistent with the rest of the UI components

 🔁 Last Updated: 2025-01-10 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import * as React from "react";
import { useState, useRef, useEffect, createContext, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronDown } from "lucide-react";

// Context for managing select state
interface SelectContextType {
  value: string;
  displayValue: string;
  setDisplayValue: (value: string) => void;
  onValueChange: (value: string) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  registerItem?: (value: string, text: string) => void;
}

const SelectContext = createContext<SelectContextType | undefined>(undefined);

const useSelect = () => {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error("Select components must be used within Select");
  }
  return context;
};

// Root component
export const Select: React.FC<{
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
}> = ({ value: controlledValue, defaultValue = "", onValueChange, children }) => {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState<string>("");
  const itemsRef = useRef<Map<string, string>>(new Map());
  const itemsRegisteredRef = useRef(0);

  const value = controlledValue !== undefined ? controlledValue : uncontrolledValue;

  const handleValueChange = (newValue: string) => {
    if (controlledValue === undefined) {
      setUncontrolledValue(newValue);
    }

    // Update displayValue immediately if the item is already registered
    // Note: If displayValue was already set by handleClick, this will ensure it's correct
    if (newValue && itemsRef.current.has(newValue)) {
      const itemText = itemsRef.current.get(newValue);
      if (itemText) {
        setDisplayValue(itemText);
      }
    } else if (!newValue || newValue === '') {
      // Value is empty, clear displayValue
      setDisplayValue("");
    }
    // If item not registered yet, don't clear displayValue here
    // It will be set by the SelectItem's handleClick or useEffect when it registers

    onValueChange?.(newValue);
    setIsOpen(false);
  };

  const registerItem = React.useCallback((itemValue: string, itemText: string) => {
    itemsRef.current.set(itemValue, itemText);

    // If this item's value matches the current selected value, update displayValue immediately
    // This is critical for initial mount when items register after the value is set
    if (itemValue === value && itemText) {
      setDisplayValue(itemText);
    }
  }, [value]);

  // Update displayValue when value changes (if item already registered)
  useEffect(() => {
    if (value && value !== '') {
      if (itemsRef.current.has(value)) {
        const itemText = itemsRef.current.get(value);
        if (itemText) {
          setDisplayValue(itemText);
        }
      } else {
        // Item not registered yet - DON'T clear displayValue
        // It will be updated when the item registers via registerItem or handleClick
        // The displayValue might have been set by handleClick already
        // Don't clear or change displayValue - wait for item to register
      }
    } else {
      // Value is empty or undefined, clear displayValue
      setDisplayValue("");
    }
  }, [value]);

  // Fallback: Poll for items to register after initial mount
  // This handles the case where value is set before SelectItems render
  useEffect(() => {
    if (value && !displayValue) {
      // Try multiple times with increasing delays to catch items that register late
      const timers: NodeJS.Timeout[] = [];

      timers.push(setTimeout(() => {
        if (itemsRef.current.has(value)) {
          const itemText = itemsRef.current.get(value);
          if (itemText) {
            setDisplayValue(itemText);
            return;
          }
        }

        // Try again after a longer delay
        timers.push(setTimeout(() => {
          if (itemsRef.current.has(value)) {
            const itemText = itemsRef.current.get(value);
            if (itemText) {
              setDisplayValue(itemText);
            }
          }
        }, 50));
      }, 0));

      return () => {
        timers.forEach(timer => clearTimeout(timer));
      };
    }
  }, [value, displayValue]);

  // Initial mount: Try to set displayValue after children render
  useEffect(() => {
    if (value && !displayValue) {
      // Use a longer delay to ensure all SelectItems have rendered and registered
      const timer = setTimeout(() => {
        if (itemsRef.current.has(value)) {
          const itemText = itemsRef.current.get(value);
          if (itemText) {
            setDisplayValue(itemText);
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []); // Only run on mount

  // Create context value object - use useMemo to ensure reference stability while allowing updates
  const contextValue = React.useMemo(() => ({
    value,
    displayValue,
    setDisplayValue,
    onValueChange: handleValueChange,
    isOpen,
    setIsOpen,
    registerItem
  }), [value, displayValue, isOpen, handleValueChange, registerItem]);

  return (
    <SelectContext.Provider value={contextValue}>
      <div className="relative">{children}</div>
    </SelectContext.Provider>
  );
};

// Trigger component
export const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { children: React.ReactNode }
>(({ className = "", children, ...props }, ref) => {
  const { isOpen, setIsOpen } = useSelect();

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => setIsOpen(!isOpen)}
      className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      {...props}
    >
      {children}
      <ChevronDown className="h-4 w-4 opacity-50" />
    </button>
  );
});
SelectTrigger.displayName = "SelectTrigger";

// Value component (shows selected value)
export const SelectValue: React.FC<{
  placeholder?: string;
  className?: string;
}> = ({ placeholder = "Select an option..." }) => {
  const { value, displayValue } = useSelect();
  // Use both value and displayValue in key to force re-render when either changes
  return <span key={`${value}-${displayValue}`} className="line-clamp-1">{value && displayValue ? displayValue : placeholder}</span>;
};

// Content component
export const SelectContent: React.FC<{
  children: React.ReactNode;
  className?: string;
  side?: 'top' | 'bottom';
}> = ({ children, className = "", side = "bottom" }) => {
  const { isOpen, setIsOpen, value, onValueChange } = useSelect();
  const contentRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, setIsOpen]);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, setIsOpen]);

  // Pass value and onValueChange to children
  // IMPORTANT: Always render children (even when hidden) so SelectItems can register
  // Only pass props to SelectItem components, not to DOM elements
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      const childType = child.type;
      const isDOMElement = typeof childType === 'string';
      const childProps = child.props as Record<string, unknown>;
      const hasValueProp = childProps && 'value' in childProps;

      if (!isDOMElement && hasValueProp) {
        return React.cloneElement(child as React.ReactElement<Record<string, unknown>>, {
          selectedValue: value,
          onSelect: onValueChange
        });
      }
      return child;
    }
    return child;
  });

  // Position classes: top = open above trigger, bottom = open below (default)
  const positionClass = side === 'top'
    ? 'bottom-full mb-1'
    : 'top-full mt-1';
  const animationY = side === 'top' ? 10 : -10;

  return (
    <>
      {/* Hidden render for registration - always rendered */}
      <div style={{ display: 'none', visibility: 'hidden', position: 'absolute' }} aria-hidden="true">
        {childrenWithProps}
      </div>

      {/* Visible dropdown - only when open */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={contentRef}
            initial={{ opacity: 0, scale: 0.95, y: animationY }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: animationY }}
            transition={{ duration: 0.15 }}
            className={`absolute z-[9999] ${positionClass} max-h-60 w-full overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md ${className}`}
            role="listbox"
          >
            {childrenWithProps}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Item component
export const SelectItem: React.FC<{
  value: string;
  children: React.ReactNode;
  className?: string;
  selectedValue?: string;
  onSelect?: (value: string) => void;
}> = ({ value, children, className = "", selectedValue, onSelect }) => {
  const isSelected = selectedValue === value;
  const { setDisplayValue, registerItem } = useSelect();
  const itemRef = useRef<HTMLButtonElement>(null);

  // Extract text content from ReactNode children
  const extractTextContent = React.useCallback((node: React.ReactNode): string => {
    if (typeof node === 'string') {
      return node;
    }
    if (typeof node === 'number') {
      return String(node);
    }
    if (React.isValidElement(node)) {
      // Cast props to access children safely in React 19
      const nodeProps = node.props as { children?: React.ReactNode };
      // Try direct children first
      if (typeof nodeProps.children === 'string') {
        return nodeProps.children;
      }
      if (typeof nodeProps.children === 'number') {
        return String(nodeProps.children);
      }
      // Try to extract from nested children (for complex ReactNodes)
      if (nodeProps.children) {
        const extract = (child: React.ReactNode): string => {
          if (typeof child === 'string') return child;
          if (typeof child === 'number') return String(child);
          if (Array.isArray(child)) {
            return child.map(extract).join('');
          }
          if (React.isValidElement(child)) {
            const childProps = child.props as { children?: React.ReactNode };
            if (childProps.children) {
              return extract(childProps.children);
            }
          }
          return '';
        };
        return extract(nodeProps.children);
      }
    }
    return '';
  }, []);

  // Register this item when children change
  useEffect(() => {
    const textContent = extractTextContent(children);
    if (registerItem && textContent) {
      // Register the item with extracted text
      registerItem(value, textContent);

      // If this is the selected item, update displayValue immediately
      if (isSelected) {
        setDisplayValue(textContent);
      }
    }
  }, [value, children, registerItem, isSelected, setDisplayValue, extractTextContent]);

  // Update displayValue when this item becomes selected (for initial load)
  // This also handles the case where items register before the value is set
  useEffect(() => {
    if (isSelected) {
      const textContent = extractTextContent(children);
      if (textContent) {
        setDisplayValue(textContent);
        // Also update the map in case it wasn't set yet
        if (registerItem) {
          registerItem(value, textContent);
        }
      }

      // Try to get from DOM as fallback (when dropdown is open)
      const timer = setTimeout(() => {
        if (itemRef.current) {
          // Clone to avoid modifying original
          const clone = itemRef.current.cloneNode(true) as HTMLElement;
          // Remove check icon
          const checkIcon = clone.querySelector('.absolute.left-2');
          if (checkIcon) {
            checkIcon.remove();
          }
          const domText = clone.textContent?.trim() || '';
          if (domText && domText !== textContent) {
            setDisplayValue(domText);
            if (registerItem) {
              registerItem(value, domText);
            }
          }
        }
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [isSelected, setDisplayValue, value, registerItem, children, extractTextContent]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Extract text content from ReactNode children (more reliable than DOM textContent)
    let textContent = extractTextContent(children);

    // Fallback: If extractTextContent returns empty, try to get from DOM
    // This handles cases where children have complex structure
    if (!textContent && itemRef.current) {
      // Clone to avoid modifying original
      const clone = itemRef.current.cloneNode(true) as HTMLElement;
      // Remove check icon if present
      const checkIcon = clone.querySelector('.absolute.left-2');
      if (checkIcon) {
        checkIcon.remove();
      }
      textContent = clone.textContent?.trim() || '';
    }

    // Update displayValue immediately before calling onSelect
    if (textContent) {
      setDisplayValue(textContent);
      // Also ensure it's registered
      if (registerItem) {
        registerItem(value, textContent);
      }
    }

    // Call onSelect which will update the value
    onSelect?.(value);
  };

  return (
    <button
      ref={itemRef}
      type="button"
      onClick={handleClick}
      className={`relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${className}`}
      role="option"
      aria-selected={isSelected}
    >
      {isSelected && (
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <Check className="h-4 w-4" />
        </span>
      )}
      {children}
    </button>
  );
};

// Group component (for organizing items)
export const SelectGroup: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div>{children}</div>;
};

// Export all components
export default Select;
